import moment from "moment-timezone";
import { rrulestr } from "rrule";
import { DsaScheduleDto, DsaScheduleEventDto } from "../generated/dashboard-client";
import { eventWallClock, nowInEventFrame, wallClockMillis } from "./dsaEventTime";

function isRecurring(event: DsaScheduleEventDto): boolean {
  return !!event.repeat && event.repeat !== "no-repeat" && !!event.rrule;
}

function isEventActiveNow(event: DsaScheduleEventDto, nowMs: number, screenTimezone?: string): boolean {
  if (event.removed) return false;
  if (event.startDate == null || event.endDate == null) return false;

  // every value below is a wall clock in the same frame: the event's window as it is displayed, and
  // "now" on whichever clock decides playback for this kind of event
  const wallClock = eventWallClock(event);
  const start = wallClockMillis(wallClock.start);
  const end = wallClockMillis(wallClock.end);
  const now = nowInEventFrame(event, nowMs, screenTimezone);

  if (!isRecurring(event)) {
    return now >= start && now <= end;
  }

  const durationMs = end - start;
  if (durationMs < 0) return false;

  const ruleSet = rrulestr(
    `DTSTART:${moment.utc(start).format("YYYYMMDDTHHmmss")}\nRRULE:${event.rrule}`,
    { forceset: true }
  );
  const occurrenceStart = ruleSet.before(new Date(now), true);
  if (!occurrenceStart) return false;

  const elapsed = now - occurrenceStart.getTime();
  return elapsed >= 0 && elapsed <= durationMs;
}

export function findActiveScheduleEvent(
  schedule: DsaScheduleDto,
  nowMs: number,
  screenTimezone?: string
): DsaScheduleEventDto | undefined {
  return (schedule.events ?? [])
    .filter(event => isEventActiveNow(event, nowMs, screenTimezone))
    .sort((a, b) => wallClockMillis(eventWallClock(a).start) - wallClockMillis(eventWallClock(b).start))[0];
}
